import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import FestivalFormModal from "@/components/FestivalFormModal";
import FestivalsCalendar from "@/components/FestivalsCalendar";
import FestivalDetailModal from "@/components/FestivalDetailModal";
import FestivalProjectsModal from "@/components/FestivalProjectsModal";

/**
 * Festivales · página compartida (bajo /festivals).
 *
 * Muestra el catálogo global de festivales, mercados y pitch forums con
 * filtros por tipo, formato, región y estado por proyecto. Precargado con
 * 40 eventos (España + Europa + Iberoamérica + top internacionales).
 *
 * Nota: los filtros se aplican en el backend. Selección de proyecto activo
 * se hace en el header — si hay un proyecto seleccionado, aparecen las
 * columnas de "estado" (interesado/enviado/aceptado/rechazado) para
 * marcar el festival como parte del pipeline de ese proyecto.
 */

const STATUS_LABEL = {
  interested: "Me interesa",
  submitted: "Enviado",
  accepted: "Aceptado",
  rejected: "Rechazado",
  discarded: "Descartado",
};
const STATUS_STYLE = {
  interested: "bg-blue-100 text-blue-800 ring-blue-300",
  submitted: "bg-amber-100 text-amber-900 ring-amber-300",
  accepted: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  rejected: "bg-rose-100 text-rose-800 ring-rose-300",
  discarded: "bg-stone-200 text-stone-700 ring-stone-300",
};

const TYPE_LABEL = {
  festival: "Festival",
  market: "Mercado",
  pitch: "Pitch",
  forum: "Foro",
  residency: "Residencia",
  lab: "Lab",
};
const FORMAT_LABEL = {
  documental: "Documental", ficcion: "Ficción", cortometraje: "Cortometraje",
  serie: "Serie", animacion: "Animación", experimental: "Experimental",
  vr_inmersivo: "VR/Inmersivo",
};
const REGION_LABEL = {
  espana: "España", iberoamerica: "Iberoamérica", europa: "Europa",
  norteamerica: "Norteamérica", internacional: "Internacional",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function upcomingEdition(f) {
  const today = new Date().toISOString().slice(0, 10);
  const eds = (f.editions || []).filter(e => e.submission_deadline || e.event_start);
  const future = eds.filter(e => (e.submission_deadline || e.event_start || "0") >= today);
  future.sort((a, b) => (a.submission_deadline || a.event_start || "").localeCompare(b.submission_deadline || b.event_start || ""));
  return future[0] || eds[eds.length - 1] || null;
}

function daysUntil(iso) {
  if (!iso) return null;
  const today = new Date();
  const target = new Date(iso + "T23:59:59");
  const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function FestivalsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [festivals, setFestivals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingFestival, setEditingFestival] = useState(null);
  const [projectsFestival, setProjectsFestival] = useState(null);
  const [detailFestival, setDetailFestival] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [view, setView] = useState(() => {
    // Recordar entre sesiones qué vista (lista/calendario) prefiere el usuario.
    try { return localStorage.getItem("festivals:view") || "list"; } catch { return "list"; }
  });
  // Filtro opcional: "sólo mostrar festivales donde participa este proyecto".
  // No es un "proyecto activo" global — cada festival puede tener varios.
  const [filterProjectId, setFilterProjectId] = useState(() => {
    try { return localStorage.getItem("festivals:filterProject") || ""; } catch { return ""; }
  });
  const [vocab, setVocab] = useState({ types: [], formats: [], regions: [], statuses: [] });
  const [filters, setFilters] = useState({ q: "", type: "", format: "", region: "", upcoming: false });
  const [loading, setLoading] = useState(true);

  // Cargar vocab + proyectos disponibles al montar
  useEffect(() => {
    api.get("/festivals-meta/vocab").then(r => setVocab(r.data)).catch(() => {});
    api.get("/icaa/projects").then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  // Recargar festivales cuando cambian filtros. Siempre pedimos /festivals
  // (que ya trae project_statuses agrupados). El filtro por proyecto se
  // aplica en cliente porque un festival puede tener varios proyectos.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.type) params.type = filters.type;
    if (filters.format) params.format = filters.format;
    if (filters.region) params.region = filters.region;
    if (filters.upcoming) params.upcoming = true;

    api.get("/festivals", { params })
      .then(r => setFestivals(r.data.festivals || []))
      .catch(() => setFestivals([]))
      .finally(() => setLoading(false));
  }, [filters, reloadTrigger]);

  // Festivales filtrados por proyecto (client-side)
  const visibleFestivals = useMemo(() => {
    if (!filterProjectId) return festivals;
    return festivals.filter(f => (f.project_statuses || []).some(s => s.project_id === filterProjectId));
  }, [festivals, filterProjectId]);

  const openCreate = () => { setEditingFestival(null); setFormOpen(true); };
  const openEdit = (f) => { setEditingFestival(f); setFormOpen(true); };
  const onSaved = () => { setReloadTrigger(t => t + 1); };
  const deleteFestival = async (f) => {
    if (!window.confirm(`¿Borrar "${f.name}" del catálogo? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/festivals/${f.id}`);
      setReloadTrigger(t => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error al borrar.");
    }
  };

  const openProjects = (f) => setProjectsFestival(f);
  const onProjectsSaved = (updated) => {
    setFestivals(list => list.map(f => f.id === updated.id ? updated : f));
  };

  return (
    <div className="min-h-screen bg-gb-bg p-6">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="gb-eyebrow">Compartido</p>
          <h1 className="gb-display mt-1 text-3xl">Festivales y eventos</h1>
          <p className="mt-2 max-w-2xl text-sm text-gb-muted">
            Catálogo de festivales, mercados y pitch forums curado. {visibleFestivals.length} de {festivals.length} eventos.
            {" "}En cada festival puedes ligar <strong>uno o varios proyectos</strong> del estudio con su propio estado (interesa/enviado/aceptado/rechazado), flags <strong>✉️ Inscritos</strong> / <strong>🎟️ Asistimos</strong> y <strong>notas</strong>.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex overflow-hidden rounded-full border border-stone-400 bg-white">
            <button onClick={() => { setView("list"); try { localStorage.setItem("festivals:view", "list"); } catch (_e) { /* ignore */ } }}
                    data-testid="festivals-view-list"
                    className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition ${view === "list" ? "bg-[#941620] text-white" : "text-stone-600 hover:text-[#941620]"}`}>
              ☰ Lista
            </button>
            <button onClick={() => { setView("calendar"); try { localStorage.setItem("festivals:view", "calendar"); } catch (_e) { /* ignore */ } }}
                    data-testid="festivals-view-calendar"
                    className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition ${view === "calendar" ? "bg-[#941620] text-white" : "text-stone-600 hover:text-[#941620]"}`}>
              ▦ Calendario
            </button>
          </div>
          {isAdmin && (
            <button onClick={openCreate}
                    data-testid="festivals-add-btn"
                    className="rounded-full bg-[#941620] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1119]">
              + Añadir festival
            </button>
          )}
        </div>
      </header>

      {/* Barra de filtros */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-gb-paper bg-white p-3">
        <input
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          placeholder="Buscar por nombre, ciudad, tag…"
          data-testid="festivals-search"
          className="min-w-[220px] flex-1 rounded-md border border-stone-300 px-3 py-1.5 text-sm outline-none focus:border-gb-red"
        />
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                data-testid="festivals-filter-type"
                className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
          <option value="">Todos los tipos</option>
          {vocab.types.map(t => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
        </select>
        <select value={filters.format} onChange={e => setFilters(f => ({ ...f, format: e.target.value }))}
                data-testid="festivals-filter-format"
                className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
          <option value="">Todos los formatos</option>
          {vocab.formats.map(f => <option key={f} value={f}>{FORMAT_LABEL[f] || f}</option>)}
        </select>
        <select value={filters.region} onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}
                data-testid="festivals-filter-region"
                className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
          <option value="">Todas las regiones</option>
          {vocab.regions.map(r => <option key={r} value={r}>{REGION_LABEL[r] || r}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input type="checkbox" checked={filters.upcoming}
                 onChange={e => setFilters(f => ({ ...f, upcoming: e.target.checked }))}
                 data-testid="festivals-filter-upcoming"
                 className="h-4 w-4 accent-gb-red" />
          Inscripción abierta
        </label>
        <div className="ml-auto flex items-center gap-2 border-l border-stone-200 pl-2">
          <label className="text-[11px] uppercase tracking-widest text-stone-500">Filtro proyecto</label>
          <select value={filterProjectId} onChange={e => {
                    const v = e.target.value;
                    setFilterProjectId(v);
                    try { localStorage.setItem("festivals:filterProject", v); } catch (_err) { /* ignore */ }
                  }}
                  data-testid="festivals-filter-project"
                  title="Muestra solo festivales donde ya has ligado este proyecto"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="">Todos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
      </div>

      {/* Vista: Lista (tabla) o Calendario */}
      {view === "calendar" ? (
        <FestivalsCalendar festivals={visibleFestivals} onSelect={(f) => setDetailFestival(f)} />
      ) : (
      <div className="overflow-hidden rounded-xl border border-gb-paper bg-white">
        <table className="w-full text-sm" data-testid="festivals-table">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-widest text-stone-600">
              <th className="px-4 py-2.5">Evento</th>
              <th className="px-3 py-2.5">Tipo</th>
              <th className="px-3 py-2.5">Ubicación</th>
              <th className="px-3 py-2.5">Deadline</th>
              <th className="px-3 py-2.5">Fecha evento</th>
              <th className="px-3 py-2.5">Formatos</th>
              <th className="px-3 py-2.5 min-w-[280px]">Estado / Proyectos</th>
              <th className="px-3 py-2.5">Web</th>
              {isAdmin && <th className="px-3 py-2.5 w-24">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8 + (isAdmin ? 1 : 0)} className="p-8 text-center text-stone-400">Cargando…</td></tr>
            )}
            {!loading && visibleFestivals.length === 0 && (
              <tr><td colSpan={8 + (isAdmin ? 1 : 0)} className="p-8 text-center text-stone-500">Sin resultados.</td></tr>
            )}
            {!loading && visibleFestivals.map(f => {
              const ed = upcomingEdition(f);
              const days = daysUntil(ed?.submission_deadline);
              const isUrgent = days !== null && days >= 0 && days <= 30;
              return (
                <tr key={f.id} className="border-b border-stone-100 hover:bg-stone-50/60" data-testid={`festival-row-${f.slug}`}>
                  <td className="max-w-[300px] px-4 py-3">
                    <div className="font-serif text-[15px] text-stone-900">{f.name}</div>
                    {f.description && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-stone-500">{f.description}</p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[12px] text-stone-700">{TYPE_LABEL[f.type] || f.type}</td>
                  <td className="px-3 py-3 text-[12px] text-stone-700">
                    <div>{f.country}</div>
                    {f.city && <div className="text-stone-400">{f.city}</div>}
                  </td>
                  <td className="px-3 py-3 text-[12px]">
                    {ed?.submission_deadline ? (
                      <div>
                        <div className="text-stone-800">{formatDate(ed.submission_deadline)}</div>
                        {days !== null && (
                          <div className={isUrgent ? "text-rose-700 font-semibold" : "text-stone-500"}>
                            {days >= 0 ? `${days} días` : "cerrada"}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-[12px] text-stone-700">
                    {ed?.event_start ? formatDate(ed.event_start) : "—"}
                    {!ed?.is_confirmed && ed?.event_start && (
                      <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-600" title="Fechas estimadas — verifica en la web oficial">*</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[11px] text-stone-600">
                    {(f.formats || []).map(fm => FORMAT_LABEL[fm] || fm).join(" · ")}
                  </td>
                  <td className="px-3 py-2.5">
                    <ProjectsCell
                      festival={f}
                      projects={projects}
                      onOpen={() => openProjects(f)}
                    />
                  </td>
                  <td className="px-3 py-3">
                    {f.website ? (
                      <a href={f.website} target="_blank" rel="noopener noreferrer"
                         data-testid={`festival-link-${f.slug}`}
                         className="text-[12px] text-gb-red underline-offset-2 hover:underline">Web →</a>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(f)}
                                data-testid={`festival-edit-${f.slug}`}
                                title="Editar festival"
                                className="rounded-full border border-stone-300 bg-white px-2 py-1 text-[11px] font-semibold text-stone-700 hover:border-[#941620] hover:text-[#941620]">
                          ✎
                        </button>
                        <button onClick={() => deleteFestival(f)}
                                data-testid={`festival-delete-${f.slug}`}
                                title="Borrar festival"
                                className="rounded-full border border-stone-300 bg-white px-2 py-1 text-[11px] font-semibold text-stone-700 hover:border-rose-500 hover:text-rose-600">
                          🗑
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <p className="mt-3 text-[11px] text-stone-500">
        <span className="text-amber-600">*</span> Fechas marcadas con asterisco son estimaciones basadas en el patrón anual del festival. Verifica siempre en la web oficial antes de planificar.
        {" "}<Link to="/" className="text-gb-red hover:underline">← Volver a proyectos</Link>
      </p>

      <FestivalFormModal
        open={formOpen}
        festival={editingFestival}
        onClose={() => setFormOpen(false)}
        onSaved={onSaved}
      />

      <FestivalProjectsModal
        open={!!projectsFestival}
        festival={projectsFestival}
        projects={projects}
        onClose={() => setProjectsFestival(null)}
        onSaved={onProjectsSaved}
      />

      <FestivalDetailModal
        open={!!detailFestival}
        festival={detailFestival}
        isAdmin={isAdmin}
        projects={projects}
        onClose={() => setDetailFestival(null)}
        onOpenEdit={(f) => { setDetailFestival(null); openEdit(f); }}
        onOpenNotes={(f) => { setDetailFestival(null); openProjects(f); }}
      />
    </div>
  );
}

/**
 * Celda visual "Nuestros proyectos" — muestra chips del estado general
 * del festival (Grabarte) + un bloque por proyecto ligado. Click abre el
 * modal de gestión. Siempre incluye acceso al modal, incluso sin
 * proyectos, para poder marcar "vamos como oyente sin peli".
 */
function ProjectsCell({ festival, projects, onOpen }) {
  const linked = festival.project_statuses || [];
  const titleOf = (pid) => projects?.find(p => p.id === pid)?.title || pid;
  const s = {
    interested: !!festival.studio_interested,
    attending: !!festival.studio_attending,
    discarded: !!festival.studio_discarded,
    notes: festival.studio_notes || "",
  };
  const anyStudio = s.interested || s.attending || s.discarded || s.notes;

  const cellClass = "w-full space-y-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-left hover:border-[#941620] hover:bg-[#941620]/5";
  const emptyClass = "w-full rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2 text-left text-[12px] text-stone-500 hover:border-[#941620] hover:bg-[#941620]/5 hover:text-[#941620]";

  return (
    <button onClick={onOpen}
            data-testid={`festival-projects-open-${festival.slug}`}
            className={(anyStudio || linked.length) ? cellClass : emptyClass}>
      {/* Bloque estado general del festival */}
      {anyStudio ? (
        <div className="flex flex-wrap items-center gap-1 border-b border-stone-100 pb-1.5">
          {s.interested && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-800 ring-1 ring-blue-300">⭐ Me interesa</span>}
          {s.attending && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 ring-1 ring-emerald-300">🎟️ Vamos</span>}
          {s.discarded && <span className="rounded-full bg-stone-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-700 ring-1 ring-stone-300">✖ Descartado</span>}
          {s.notes && <span title="Con notas generales" className="text-[12px]">📝</span>}
        </div>
      ) : (
        !linked.length && (
          <div className="text-[12px] text-stone-500">
            + Marcar interés / añadir proyecto
          </div>
        )
      )}

      {/* Bloque proyectos vinculados */}
      {linked.map((ps) => (
        <div key={ps.project_id} className="flex items-center justify-between gap-2 rounded-md bg-stone-50 px-2 py-1">
          <span className="truncate font-serif text-[13px] text-stone-900">{titleOf(ps.project_id)}</span>
          <div className="flex shrink-0 items-center gap-1">
            {ps.status && (
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ring-1 ${STATUS_STYLE[ps.status] || "bg-stone-100"}`}>
                {STATUS_LABEL[ps.status] || ps.status}
              </span>
            )}
            {ps.submitted && <span title="Inscritos" className="text-[13px]">✉️</span>}
            {ps.attending && <span title="Asistimos" className="text-[13px]">🎟️</span>}
            {ps.notes && <span title="Con notas" className="text-[13px]">📝</span>}
          </div>
        </div>
      ))}

      {(anyStudio || linked.length) && (
        <div className="pt-0.5 text-center text-[10px] font-semibold uppercase tracking-widest text-[#941620]">
          + gestionar
        </div>
      )}
    </button>
  );
}
